// Timeline Card Component - Pure Alpine UI component
// Handles both PropertyTimelineEntry and AuditLog items
// ActionCable subscription is managed by Stimulus (timeline-subscription controller)
//
// Usage:
//   <div data-controller="timeline-subscription"
//        data-timeline-subscription-property-id-value="123"
//        x-data="timelineCard"
//        data-property-id="123"
//        data-activity-items="[...]"
//        @timeline-subscription:message.window="handleUpdate($event.detail)">

export function timelineCard() {
  return {
    propertyId: null,
    items: [],
    filter: 'notes', // 'all', 'notes', 'system_events' - default to notes
    isSubmitting: false,
    searchQuery: '',
    searching: false,

    get filteredItems() {
      let filtered = this.items;

      // Filter out items without valid content or message
      filtered = filtered.filter(item => {
        const hasContent = item.content && item.content.trim() !== '';
        const hasMessage = item.message && item.message.trim() !== '';
        return hasContent || hasMessage;
      });

      // Apply type filter
      if (this.filter === 'notes') {
        // Notes are PropertyTimelineEntry with entry_type note/reminder/preference/rule
        filtered = filtered.filter(item =>
          item.entry_type && ['note', 'reminder', 'preference', 'rule'].includes(item.entry_type)
        );
      } else if (this.filter === 'system_events') {
        // System events are AuditLog, PropertyKeyLog, or PropertyTimelineEntry with entry_type system_event
        filtered = filtered.filter(item =>
          !item.entry_type || item.entry_type === 'system_event' || item.action || item.event_type
        );
      }

      // Apply search filter
      if (this.searchQuery.trim() !== '') {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(item => {
          const content = (item.content || item.message || '').toLowerCase();
          const typeLabel = (item.type_label || item.action || '').toLowerCase();
          const userName = (item.user?.name || '').toLowerCase();
          return content.includes(query) || typeLabel.includes(query) || userName.includes(query);
        });
      }

      return filtered;
    },

    get hasDeleteableItems() {
      return this.filteredItems.some(item =>
        item.entry_type && ['note', 'reminder', 'preference', 'rule'].includes(item.entry_type)
      );
    },

    init() {
      // Get property ID and initial data from data attributes
      this.propertyId = parseInt(this.$el.dataset.propertyId)

      // Parse initial activity items from data attribute
      const initialData = this.$el.dataset.activityItems
      if (initialData) {
        try {
          this.items = JSON.parse(initialData)
        } catch (e) {
          console.error('Failed to parse activity items data:', e)
        }
      }

      // Sort items by timestamp on init
      this.sortItems()
    },

    // Handle updates from Stimulus timeline-subscription controller
    handleUpdate(message) {
      if (message.type === 'timeline_entry') {
        this.handleTimelineEntryUpdate(message)
      } else if (message.type === 'audit_log') {
        this.handleAuditLogUpdate(message)
      } else if (message.type === 'key_log') {
        this.handleKeyLogUpdate(message)
      }
    },

    handleTimelineEntryUpdate(message) {

      switch(message.action) {
        case 'create':
          // Only add if active (we filter to active_only)
          if (message.data.active) {
            this.items.unshift(message.data);
            this.sortItems();
          }
          break;

        case 'update':
          const updateIndex = this.items.findIndex(item =>
            item.entry_type && item.id === message.id
          );
          if (updateIndex !== -1) {
            // If updated to inactive, remove it
            if (!message.data.active) {
              this.items.splice(updateIndex, 1);
            } else {
              this.items[updateIndex] = message.data;
              this.sortItems();
            }
          } else if (message.data.active) {
            // Was inactive, now active - add it
            this.items.unshift(message.data);
            this.sortItems();
          }
          break;

        case 'destroy':
          this.items = this.items.filter(item =>
            !(item.entry_type && item.id === message.id)
          );
          break;
      }
    },

    handleAuditLogUpdate(message) {
      switch(message.action) {
        case 'create':
          this.items.unshift(message.data);
          this.sortItems();
          // Limit to 20 items
          if (this.items.length > 20) {
            this.items = this.items.slice(0, 20);
          }
          break;
      }
    },

    handleKeyLogUpdate(message) {
      switch(message.action) {
        case 'create':
          // Only add important events (key created, identifier changed, etc.)
          if (message.data.important_event) {
            this.items.unshift(message.data);
            this.sortItems();
            // Limit to 20 items
            if (this.items.length > 20) {
              this.items = this.items.slice(0, 20);
            }
          }
          break;
      }
    },

    sortItems() {
      this.items.sort((a, b) => {
        const aTime = this.getItemTimestamp(a);
        const bTime = this.getItemTimestamp(b);
        return bTime - aTime; // Descending order (newest first)
      });
    },

    getItemTimestamp(item) {
      if (item.entry_type) {
        // PropertyTimelineEntry - has formatted_date or created_at
        return new Date(item.created_at).getTime();
      } else {
        // AuditLog - has created_at
        return new Date(item.created_at).getTime();
      }
    },

    isTimelineEntry(item) {
      return !!item.entry_type;
    },

    isAuditLog(item) {
      return !!item.action && !item.entry_type;
    },

    getIcon(item) {
      if (this.isTimelineEntry(item)) {
        return item.icon || '•';
      } else {
        // Audit log - clipboard icon
        return `<svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
        </svg>`;
      }
    },

    getBadgeClasses(item) {
      if (this.isTimelineEntry(item)) {
        if (item.entry_type === 'system_event') {
          return 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
        }
        return 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400';
      } else {
        return 'bg-gray-50 text-gray-700 dark:bg-gray-800/50 dark:text-gray-400';
      }
    },

    getIconBgClasses(item) {
      if (this.isTimelineEntry(item)) {
        if (item.entry_type === 'system_event') {
          return 'bg-blue-100 dark:bg-blue-900/30';
        }
        return 'bg-purple-100 dark:bg-purple-900/30';
      } else {
        return 'bg-gray-100 dark:bg-gray-800/50';
      }
    },

    renderItem(item) {
      // Use renderTimelineItem method
      return this.renderTimelineItem(item, this.hasDeleteableItems)
    },

    // Render a timeline item as table cells
    renderTimelineItem(item, hasDeleteableItems = false) {
      const isNote = item.entry_type && ['note', 'reminder', 'preference', 'rule'].includes(item.entry_type)
      const badgeClasses = isNote ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400' : 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'

      // Format the created_at date as "18 Oct 2025"
      const createdDate = new Date(item.created_at)
      const formattedDate = createdDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

      // Build secondary info
      let secondaryInfo = ''

      if (item.entry_type === 'reminder' && item.reminder_date) {
        const reminderDate = new Date(item.reminder_date)
        const hasTime = reminderDate.getHours() !== 0 || reminderDate.getMinutes() !== 0
        const reminderFormattedDate = reminderDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        const formattedTime = hasTime ? reminderDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''
        const dateTimeDisplay = formattedTime ? `${reminderFormattedDate} at ${formattedTime}` : reminderFormattedDate
        secondaryInfo += `📅 ${dateTimeDisplay}`
      } else if (item.entry_type === 'preference' && item.contractor_name) {
        secondaryInfo += `👤 ${item.contractor_name}`
      }

      // Only add user info if we have a valid name
      if (item.user && item.user.name && item.user.name !== 'undefined') {
        if (secondaryInfo) secondaryInfo += ' • '
        secondaryInfo += `by ${item.user.name}`
      }

      // Check if this is a user-generated entry (not a system event)
      const isUserGenerated = item.entry_type && ['note', 'reminder', 'preference', 'rule'].includes(item.entry_type)

      // Table cells with Date column first
      const displayContent = item.content || item.message
      const contentHtml = item.gmail_url
        ? `<a href="${item.gmail_url}" target="_blank" rel="noopener noreferrer" class="hover:underline">${displayContent}</a> <a href="${item.gmail_url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center ml-1 text-gray-400 hover:text-blue-600" title="Open in Gmail"><i class="ph ph-envelope-simple text-xs"></i></a>`
        : displayContent

      let html = `
        <td class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">
          ${formattedDate}
        </td>
        <td class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">
          <div${secondaryInfo ? ' class="mb-1"' : ''}>${contentHtml}</div>
          ${secondaryInfo ? `<div class="text-xs text-gray-600 dark:text-gray-400">${secondaryInfo}</div>` : ''}
        </td>
        <td class="px-4 py-3 text-sm">
          <div class="flex justify-end items-center">
            <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${badgeClasses}">
              ${item.type_label || item.action || 'System Event'}
            </span>
          </div>
        </td>`

      // Only add Actions column if there are deleteable items in the view
      if (hasDeleteableItems) {
        html += `
        <td class="px-4 py-3">
          <div class="flex justify-end items-center">
            ${isUserGenerated ? '<button type="button" onclick="window.deleteTimelineEntry(' + item.id + ', ' + this.propertyId + ')" class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"><svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>' : ''}
          </div>
        </td>`
      }

      return html
    },

    // Delete timeline entry
    deleteEntry(entryId) {
      if (!confirm('Are you sure you want to delete this timeline entry?')) {
        return
      }

      fetch(`/properties/${this.propertyId}/timeline_entries/${entryId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })
      .then(response => {
        if (!response.ok) {
          alert('Failed to delete timeline entry')
        }
        // Don't manually remove from array - ActionCable will handle it via the 'destroy' message
      })
      .catch(error => {
        console.error('Error:', error)
        alert('Failed to delete timeline entry')
      })
    },

    // Handle form submission start
    handleFormSubmit() {
      this.isSubmitting = true
    },

    // Handle form submission complete
    handleFormComplete(event) {
      const { fetchResponse } = event.detail
      // Reset loading state regardless of success/failure
      this.isSubmitting = false

      // Clear form if submission was successful (2xx status)
      if (fetchResponse && (fetchResponse.succeeded || fetchResponse.response?.ok)) {
        const textarea = document.getElementById('timeline-entry-content')
        if (textarea) {
          textarea.value = ''
          textarea.focus()
        }
      }
    }
  }
}

// Global helper for delete button onclick (since we use innerHTML for table rows)
window.deleteTimelineEntry = function(entryId, propertyId) {
  if (!confirm('Are you sure you want to delete this timeline entry?')) {
    return
  }

  fetch(`/properties/${propertyId}/timeline_entries/${entryId}`, {
    method: 'DELETE',
    headers: {
      'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      'Accept': 'application/json'
    }
  })
  .then(response => {
    if (!response.ok) {
      alert('Failed to delete timeline entry')
    }
    // Don't manually remove from array - ActionCable will handle it via the 'destroy' message
  })
  .catch(error => {
    console.error('Error:', error)
    alert('Failed to delete timeline entry')
  })
}
